<template>
  <div>
    <!-- 主登录弹窗 -->
    <el-dialog
      :visible="modalShow"
      width="480px"
      :show-close="true"
      custom-class="login-dialog"
      @close="cancel"
    >
      <div class="login-container">
        <!-- 标题 -->
        <div class="login-header">
          <span class="login-logo">🔐</span>
          <h2 class="login-title">供应商登录</h2>
        </div>

        <!-- Tab 切换 -->
        <div class="login-tabs">
          <div
            class="login-tab-item"
            :class="{ active: activeTab === 'account' }"
            @click="switchTab('account')"
          >账号密码登录</div>
          <div
            class="login-tab-item"
            :class="{ active: activeTab === 'phone' }"
            @click="switchTab('phone')"
          >手机号验证码登录</div>
        </div>

        <!-- 账号密码登录 -->
        <el-form
          v-show="activeTab === 'account'"
          :model="accountForm"
          :rules="accountRules"
          ref="accountForm"
          label-width="0"
        >
          <el-form-item prop="userName">
            <el-input
              v-model="accountForm.userName"
              placeholder="请输入账号"
              prefix-icon="el-icon-user"
              autocomplete="off"
              @keyup.enter.native="openCaptcha"
              clearable
            />
          </el-form-item>
          <el-form-item prop="pass">
            <el-input
              type="password"
              v-model="accountForm.pass"
              placeholder="请输入密码"
              prefix-icon="el-icon-lock"
              autocomplete="off"
              @keyup.enter.native="openCaptcha"
              show-password
              clearable
            />
          </el-form-item>
          <div class="btn-group">
            <el-button type="primary" class="btn-login" @click="openCaptcha">登 录</el-button>
            <el-button class="btn-reset" @click="resetAccountForm">重 置</el-button>
          </div>
        </el-form>

        <!-- 手机号验证码登录 -->
        <el-form
          v-show="activeTab === 'phone'"
          :model="phoneForm"
          :rules="phoneRules"
          ref="phoneForm"
          label-width="0"
        >
          <el-form-item prop="phone">
            <el-input
              v-model="phoneForm.phone"
              placeholder="请输入手机号"
              prefix-icon="el-icon-mobile-phone"
              autocomplete="off"
              @keyup.enter.native="submitPhoneForm"
              clearable
            />
          </el-form-item>
          <el-form-item prop="smsCode">
            <div class="sms-wrap">
              <el-input
                v-model="phoneForm.smsCode"
                placeholder="请输入短信验证码"
                prefix-icon="el-icon-message"
                autocomplete="off"
                @keyup.enter.native="submitPhoneForm"
                clearable
              />
              <el-button
                class="btn-sms"
                :disabled="smsCooldown > 0 || smsSending"
                :loading="smsSending"
                @click="sendSmsCode"
              >
                {{ smsCooldown > 0 ? `${smsCooldown}s 后重发` : '获取验证码' }}
              </el-button>
            </div>
          </el-form-item>
          <div class="btn-group">
            <el-button type="primary" class="btn-login" @click="submitPhoneForm">登 录</el-button>
            <el-button class="btn-reset" @click="resetPhoneForm">重 置</el-button>
          </div>
        </el-form>
      </div>
    </el-dialog>

    <!-- 供应商选择弹窗（一个手机号对应多个供应商时使用） -->
    <el-dialog
      :visible.sync="selectSupplierVisible"
      width="500px"
      custom-class="select-supplier-dialog"
      :append-to-body="true"
      @close="closeSelectSupplier"
    >
      <div class="select-supplier-header">
        <span class="select-icon">🏢</span>
        <div class="select-title-text">选择供应商</div>
      </div>
      <div class="select-supplier-body">
        <p class="select-tip">该手机号关联了以下供应商，请选择要登录的账户：</p>
        <el-radio-group v-model="selectedSupplierHid" class="supplier-radio-group">
          <div
            v-for="supplier in supplierList"
            :key="supplier.hid"
            class="supplier-radio-item"
          >
            <el-radio :label="supplier.hid" border>
              <div class="supplier-info">
                <div class="supplier-name">{{ supplier.hName }}</div>
                <div class="supplier-credit">税号/身份证号：{{ supplier.hCreditCode }}</div>
              </div>
            </el-radio>
          </div>
        </el-radio-group>
      </div>
      <div slot="footer" class="select-supplier-footer">
        <el-button @click="closeSelectSupplier">取 消</el-button>
        <el-button type="primary" @click="confirmSelectSupplier">确认登录</el-button>
      </div>
    </el-dialog>

    <!-- 滑动验证弹窗（发送短信验证码前使用） -->
    <el-dialog
      :visible.sync="slideVerifyVisible"
      width="360px"
      custom-class="captcha-dialog"
      :append-to-body="true"
      title="请完成安全验证"
      @close="slideVerifyVisible = false"
    >
      <slide-verify
        ref="slideVerify"
        :accuracy="5"
        :show="true"
        :imgs="slideImgs"
        slider-text="向右滑动"
        @success="onSlideSuccess"
        @fail="onSlideFail"
        @refresh="onSlideRefresh"
      />
    </el-dialog>

    <!-- 图形验证码二次确认弹窗 -->
    <el-dialog
      :visible.sync="captchaVisible"
      width="360px"
      custom-class="captcha-dialog"
      :append-to-body="true"
      @close="closeCaptcha"
    >
      <div class="captcha-header">
        <span class="captcha-icon">🛡️</span>
        <div class="captcha-title-text">安全验证</div>
      </div>
      <div class="captcha-body">
        <p class="captcha-tip"></p>
        <div class="captcha-row">
          <el-input
            v-model="captchaCode"
            placeholder="请输入验证码"
            size="medium"
            prefix-icon="el-icon-key"
            autocomplete="off"
            @keyup.enter.native="doAccountLogin"
            clearable
          />
          <img
            v-if="codeUrl"
            :src="codeUrl"
            @click="getCode"
            class="captcha-img"
            title="点击刷新"
          />
        </div>
      </div>
      <div slot="footer" class="captcha-footer">
        <el-button @click="closeCaptcha">取 消</el-button>
        <el-button type="primary" @click="doAccountLogin">确认登录</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCodeImg, loginByPhone, sendLoginSmsCode, loginByPhoneSelect } from "@/api/login";
import { setToken } from "@/utils/auth";
import { bus } from "@/main";

export default {
  props: {
    modalShow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const validatePhone = (rule, value, callback) => {
      if (!value) {
        callback(new Error("手机号不能为空"));
      } else if (!/^1[3-9]\d{9}$/.test(value)) {
        callback(new Error("请输入正确的手机号码"));
      } else {
        callback();
      }
    };
    return {
      activeTab: "account",
      // 账号密码表单（不含验证码字段）
      accountForm: { pass: "", userName: "" },
      accountRules: {
        userName: [{ required: true, message: "账号不能为空", trigger: "blur" }],
        pass: [{ required: true, message: "密码不能为空", trigger: "blur" }],
      },
      // 图形验证码弹窗
      captchaVisible: false,
      captchaCode: "",
      codeUrl: "",
      captchaUuid: "",
      // 手机号表单
      phoneForm: { phone: "", smsCode: "" },
      phoneRules: {
        phone: [{ required: true, validator: validatePhone, trigger: "blur" }],
        smsCode: [{ required: true, message: "验证码不能为空", trigger: "blur" }],
      },
      smsCooldown: 0,
      cooldownTimer: null,
      smsSending: false,
      slideVerifyVisible: false,
      slideImgs: [
        '/vendor/slide-verify-imgs/img.8e16743663897de6490cbd9bfd412159.jpg',
        '/vendor/slide-verify-imgs/img1.f79e1d9ee0af670da4323145918396cf.jpg',
        '/vendor/slide-verify-imgs/img2.491a61707db4784a370c3e7049b4beee.jpg',
        '/vendor/slide-verify-imgs/img3.fed1c6632e3f10ee202eb35b511a6363.jpg',
        '/vendor/slide-verify-imgs/img4.397bc48e54eab8eab87df68b54596bb5.jpg',
        '/vendor/slide-verify-imgs/img5.64c55a839fdabdf119a67aff089f7e2a.jpg',
      ],
      // 供应商选择相关
      selectSupplierVisible: false,
      supplierList: [],
      selectedSupplierHid: null,
    };
  },
  mounted() {
    const expire = parseInt(localStorage.getItem('sms_login_cooldown_expire'));
    if (expire) {
      const remaining = Math.ceil((expire - Date.now()) / 1000);
      if (remaining > 0) {
        this.smsCooldown = remaining;
        this.cooldownTimer = setInterval(() => {
          this.smsCooldown--;
          if (this.smsCooldown <= 0) {
            clearInterval(this.cooldownTimer);
            this.cooldownTimer = null;
            localStorage.removeItem('sms_login_cooldown_expire');
          }
        }, 1000);
      } else {
        localStorage.removeItem('sms_login_cooldown_expire');
      }
    }
  },
  watch: {
    modalShow: {
      handler(newVal) {
        if (!newVal) this.resetAll();
      },
    },
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab;
    },
    resetAll() {
      this.activeTab = "account";
      this.captchaVisible = false;
      this.captchaCode = "";
      this.codeUrl = "";
      this.captchaUuid = "";
      this.accountForm = { pass: "", userName: "" };
      this.phoneForm = { phone: "", smsCode: "" };
      this.selectSupplierVisible = false;
      this.supplierList = [];
      this.selectedSupplierHid = null;
      this.$nextTick(() => {
        if (this.$refs.accountForm) this.$refs.accountForm.clearValidate();
        if (this.$refs.phoneForm) this.$refs.phoneForm.clearValidate();
      });
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
        this.smsCooldown = 0;
        this.cooldownTimer = null;
      }
    },
    // 获取图形验证码
    getCode() {
      getCodeImg().then((res) => {
        this.codeUrl = "data:image/gif;base64," + res.img;
        this.captchaUuid = res.uuid;
      });
    },
    // 账号密码校验通过后，打开验证码弹窗
    openCaptcha() {
      this.$refs.accountForm.validate((valid) => {
        if (valid) {
          this.captchaCode = "";
          this.captchaVisible = true;
          this.getCode();
        }
      });
    },
    // 关闭验证码弹窗
    closeCaptcha() {
      this.captchaVisible = false;
      this.captchaCode = "";
    },
    // 携带验证码执行真正的登录请求
    doAccountLogin() {
      if (!this.captchaCode.trim()) {
        this.$message.warning("请输入验证码");
        return;
      }
      const payload = {
        userName: this.accountForm.userName,
        pass: this.accountForm.pass,
        code: this.captchaCode,
        uuid: this.captchaUuid,
      };
      this.$store
        .dispatch("Login", payload)
        .then(() => {
          this.captchaVisible = false;
          bus.$emit("update:login", true, this.$route.name);
          this.$emit("success");
          this.$message.success("登录成功！");
          this.cancel();
        })
        .catch(() => {
          // 登录失败刷新验证码
          this.getCode();
          this.captchaCode = "";
        });
    },
    resetAccountForm() {
      this.$refs.accountForm.resetFields();
    },
    // 发送短信验证码 - 先弹滑动验证
    sendSmsCode() {
      this.$refs.phoneForm.validateField("phone", (errMsg) => {
        if (!errMsg) {
          this.slideVerifyVisible = true;
          this.$nextTick(() => {
            this.$refs.slideVerify && this.$refs.slideVerify.reset();
          });
        }
      });
    },
    onSlideSuccess() {
      this.slideVerifyVisible = false;
      this.smsSending = true;
      sendLoginSmsCode(this.phoneForm.phone)
        .then(() => {
          this.$message.success("验证码已发送，请注意查收！");
          this.smsCooldown = 60;
          localStorage.setItem('sms_login_cooldown_expire', Date.now() + 60 * 1000);
          this.cooldownTimer = setInterval(() => {
            this.smsCooldown--;
            if (this.smsCooldown <= 0) {
              clearInterval(this.cooldownTimer);
              this.cooldownTimer = null;
              localStorage.removeItem('sms_login_cooldown_expire');
            }
          }, 1000);
        })
        .catch(() => {})
        .finally(() => {
          this.smsSending = false;
        });
    },
    onSlideFail() {
      this.$message.warning("验证失败，请重试");
    },
    onSlideRefresh() {},
    // 手机号验证码登录
    submitPhoneForm() {
      this.$refs.phoneForm.validate((valid) => {
        if (valid) {
          loginByPhone(this.phoneForm.phone, this.phoneForm.smsCode)
            .then((res) => {
              // 判断是否需要选择供应商
              if (res.needSelect && res.suppliers && res.suppliers.length > 0) {
                // 需要选择供应商
                this.supplierList = res.suppliers;
                this.selectedSupplierHid = null;
                this.selectSupplierVisible = true;
              } else {
                // 不需要选择，直接登录成功
                this.handlePhoneLoginSuccess(res);
              }
            })
            .catch(() => {});
        }
      });
    },
    // 处理手机号登录成功
    handlePhoneLoginSuccess(res) {
      this.$store.commit("SET_TOKEN", res.token);
      setToken(res.token);
      sessionStorage.setItem("bsSupplier", JSON.stringify(res.bsSupplier));
      bus.$emit("update:login", true, this.$route.name);
      this.$emit("success");
      this.$message.success("登录成功！");
      this.cancel();
    },
    // 关闭供应商选择弹窗
    closeSelectSupplier() {
      this.selectSupplierVisible = false;
      this.supplierList = [];
      this.selectedSupplierHid = null;
    },
    // 确认选择供应商并登录
    confirmSelectSupplier() {
      if (!this.selectedSupplierHid) {
        this.$message.warning("请选择要登录的供应商");
        return;
      }
      // 调用选定供应商登录接口
      loginByPhoneSelect(this.phoneForm.phone, this.selectedSupplierHid)
        .then((res) => {
          this.closeSelectSupplier();
          this.handlePhoneLoginSuccess(res);
        })
        .catch(() => {});
    },
    resetPhoneForm() {
      this.$refs.phoneForm.resetFields();
    },
    cancel() {
      this.$emit("update:modalShow");
    },
  },
  beforeDestroy() {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
    }
  },
};
</script>

<style>
/* 主登录弹窗 */
.login-dialog .el-dialog__header {
  padding: 0;
}
.login-dialog .el-dialog__body {
  padding: 0;
  overflow: hidden;
}
.login-dialog {
  border-radius: 12px;
  overflow: hidden;
}
/* 验证码确认弹窗 */
.captcha-dialog {
  border-radius: 12px;
  overflow: hidden;
}
.captcha-dialog .el-dialog__header {
  padding: 0;
  border-bottom: 1px solid #e8eaed;
}
.captcha-dialog .el-dialog__title {
  display: none;
}
.captcha-dialog .el-dialog__headerbtn {
  display: none;
}
.captcha-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%);
  gap: 8px;
}
.captcha-icon {
  font-size: 20px;
}
.captcha-title-text {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  letter-spacing: 1px;
}
.captcha-dialog .el-dialog__body {
  padding: 24px 24px 16px;
  background: #fff;
}
.captcha-dialog .el-dialog__footer {
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafbfc;
}
/* 供应商选择弹窗 */
.select-supplier-dialog {
  border-radius: 12px;
  overflow: hidden;
}
.select-supplier-dialog .el-dialog__header {
  padding: 0;
  border-bottom: 1px solid #e8eaed;
}
.select-supplier-dialog .el-dialog__title {
  display: none;
}
.select-supplier-dialog .el-dialog__headerbtn {
  display: none;
}
.select-supplier-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%);
  gap: 8px;
}
.select-icon {
  font-size: 20px;
}
.select-title-text {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  letter-spacing: 1px;
}
.select-supplier-dialog .el-dialog__body {
  padding: 24px 24px 16px;
  background: #fff;
}
.select-supplier-body {
  max-height: 400px;
  overflow-y: auto;
}
.select-tip {
  font-size: 14px;
  color: #606266;
  margin: 0 0 16px;
  line-height: 1.5;
}
.supplier-radio-group {
  display: block;
}
.supplier-radio-item {
  margin-bottom: 12px;
}
.supplier-radio-item .el-radio {
  width: 100%;
  height: auto;
  margin: 0;
  padding: 12px 16px;
  line-height: 1.5;
}
.supplier-info {
  margin-left: 8px;
}
.supplier-name {
  font-size: 15px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}
.supplier-credit {
  font-size: 12px;
  color: #909399;
}
.select-supplier-dialog .el-dialog__footer {
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafbfc;
}
</style>

<style scoped>
.login-container {
  padding: 36px 44px 32px;
  background: #fff;
}

/* 标题区 */
.login-header {
  text-align: center;
  margin-bottom: 28px;
}
.login-logo {
  font-size: 36px;
  display: block;
  margin-bottom: 8px;
}
.login-title {
  font-size: 22px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

/* Tab */
.login-tabs {
  display: flex;
  border-bottom: 2px solid #e4e7ed;
  margin-bottom: 24px;
}
.login-tab-item {
  flex: 1;
  text-align: center;
  padding: 10px 0;
  font-size: 15px;
  color: #909399;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
  user-select: none;
}
.login-tab-item:hover {
  color: #409eff;
}
.login-tab-item.active {
  color: #409eff;
  font-weight: 600;
}
.login-tab-item.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 20%;
  width: 60%;
  height: 2px;
  background: #409eff;
  border-radius: 2px;
}

/* 短信行 */
.sms-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sms-wrap .el-input {
  flex: 1;
}
.btn-sms {
  min-width: 110px;
  flex-shrink: 0;
  white-space: nowrap;
}

/* 按钮组 */
.btn-group {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}
.btn-login {
  flex: 2;
  height: 42px;
  font-size: 15px;
  letter-spacing: 4px;
}
.btn-reset {
  flex: 1;
  height: 42px;
  font-size: 15px;
  letter-spacing: 2px;
}

/* 验证码弹窗内容 */
.captcha-tip {
  font-size: 13px;
  color: #606266;
  margin: 0 0 18px;
  text-align: center;
}
.captcha-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.captcha-row .el-input {
  flex: 1;
}
.captcha-img {
  height: 42px;
  min-width: 120px;
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.2s, transform 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.captcha-img:hover {
  opacity: 0.75;
  transform: scale(1.02);
}
.captcha-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
}
</style>
